//Hooks
import { get } from 'svelte/store';
//Helpers
import {
	real_id,
	isProThreeEM,
	isBluTRV,
	isPill,
	isShellyPresence,
	get_mode,
} from 'src/helpers/interface_helpers';
import {
	doesSupportVirtualLocalActions,
	getVirtualLocalControlOptions,
} from './local_action_virtual_helpers';
import { getComponentIdByDeviceChannel } from './local_action_components_helpers';
import {
	getSwitchPeripheralsChannel,
	isPeripheralDevice,
} from 'src/helpers/addons/addon_device_helpers';
// Stores
import { devices_status, devices_settings } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
// Device Local Actions
import { getCuryLocalActions } from '../devices/cury_local_actions';
import { getMediaPlayerLocalActions } from '../devices/media_player_local_actions';
import { getBreakerLocalActions } from '../devices/breaker_local_actions';
import { getCameraLocalActions } from '../devices/camera_local_actions';
import { getLightLocalActions } from '../devices/lights_local_actions';
import { getRollerLocalActions } from '../devices/rollers_local_actions';
import { getPresenceLocalActions } from '../devices/presence_local_actions';
import { getSwitchLocalActions } from '../devices/switch_local_actions';
import { getBluTRVLocalActions, getTRVLocalActions } from '../devices/trv_local_actions';
import { isXT1Module } from 'src/helpers/xt1_helpers/template_helpers';
import { isVirtualDevice } from 'src/helpers/virtual_helpers/virtual_components_helpers';
import { doesSupportLocalActions } from '../supported/local_action_supported_helpers';
//Types
import type { Device } from 'src/types/Device';
import type { ActionJob, LocalControlOptions } from 'src/types/requests/schedules/schedules_g2';
import type { Peripheral } from 'src/types/Peripherals';

export const createActionJob = (device: Device, switchPeripheral: Peripheral = null): ActionJob => {
	const action: ActionJob = { method: '', params: {} },
		controlOption = getLocalControlOptions(action, device, switchPeripheral)?.[0];
	if (controlOption) {
		action.method = controlOption.value;
		action.params.id = controlOption.params.id;
		if (controlOption.onOffState?.checked) {
			action.params.on = controlOption.params.on;
		}
		for (const attr in controlOption.attr) {
			if (controlOption.attr[attr] === true && controlOption.params[attr] != null) {
				action.params[attr] = controlOption.params[attr];
			}
		}
	}
	return action;
};

export const getLocalControlOptions = (
	action: ActionJob,
	device: Device,
	switchPeripheral: Peripheral = null,
	scheduleAction: boolean = false,
): LocalControlOptions[] => {
	let mainDevice: Device;
	if (isVirtualDevice(device.type) || isPeripheralDevice(device.type)) {
		const allDevices = get(devices);
		mainDevice = allDevices[real_id(device.id)] || device;
	} else {
		mainDevice = device;
	}
	let deviceChannel = device.category == 'emeter' ? 0 : getComponentIdByDeviceChannel(mainDevice);
	if (isPeripheralDevice(device.type) && device.peripheral_type === 'switch') {
		deviceChannel = Number(device.component.split(':')[1] ?? 100);
	} else if (isPill(mainDevice.type)) {
		deviceChannel = getSwitchPeripheralsChannel(mainDevice.id)?.[0] ?? 200;
	} else if (isShellyPresence(mainDevice.type)) {
		deviceChannel = 200;
	} else if (switchPeripheral) {
		deviceChannel = switchPeripheral?.sensor?.extid ?? 100;
	} else if (isProThreeEM(mainDevice.type)) {
		deviceChannel = 100;
	}
	const params = {
		id: action.params?.id != undefined ? action.params?.id : deviceChannel,
	};
	let localControlOptions: LocalControlOptions[] = [];
	const deviceMode = get_mode(
		mainDevice,
		devices_status.getByRealId(mainDevice.id),
		devices_settings.getByRealId(mainDevice.id),
	);
	const isModuleInCctMode =
		mainDevice.category === 'module' && (deviceMode === 'cct' || mainDevice.type === 'S3MX-0A');

	switch (mainDevice.category) {
		case 'roller':
			localControlOptions = getRollerLocalActions(action, mainDevice, params);
			break;
		case 'light':
			localControlOptions = getLightLocalActions(action, mainDevice, params, scheduleAction);
			break;
		case 'thermostat':
			if (isBluTRV(device.type)) {
				return getBluTRVLocalActions(action, device, scheduleAction);
			} else if (device.type == 'SHTRV-01') {
				return getTRVLocalActions(action);
			}
			break;
		case 'diffuser':
			localControlOptions = getCuryLocalActions(action, localControlOptions);
			break;
		case 'breaker':
			localControlOptions = getBreakerLocalActions(action, params);
			break;
		case 'media_player':
			return getMediaPlayerLocalActions(action, localControlOptions);
		case 'camera':
			localControlOptions = getCameraLocalActions(action, mainDevice, params);
			break;
		default:
			if (isModuleInCctMode) {
				localControlOptions = getLightLocalActions(action, mainDevice, params, scheduleAction);
			} else if (isShellyPresence(device.type)) {
				localControlOptions = getPresenceLocalActions(action, device, localControlOptions);
			} else if (doesSupportLocalActions(mainDevice)) {
				localControlOptions = getSwitchLocalActions(action, mainDevice, params);
			}
			break;
	}
	if (doesSupportVirtualLocalActions(mainDevice)) {
		const virtualLocalControlOptions: LocalControlOptions = getVirtualLocalControlOptions(
			action,
			mainDevice,
		);
		isVirtualDevice(device.type) || isXT1Module(device.type)
			? localControlOptions.unshift(virtualLocalControlOptions)
			: localControlOptions.push(virtualLocalControlOptions);
	}
	return localControlOptions;
};
